// Zugriff auf das Backend. Eine Funktion je Endpunkt; der einzige Zustand ist der Zähler laufender Anfragen.

import type {
  Anliegen,
  AnliegenStatus,
  Benutzer,
  BereichId,
  Ruecknahme,
  Artikel,
  ArtikelEingabe,
  ArtikelgruppeEingabe,
  ArtikelSammelAntwort,
  Beobachtung,
  Bestellabgleich,
  Bestellung,
  BestellungEingabe,
  Bestellvorschlag,
  ChatEreignis,
  Datenpruefung,
  Disposition,
  Erledigung,
  EinordnenAntwort,
  Entscheidung,
  ErfassungAntwort,
  ErfassungVorlage,
  ExcelImport,
  Faellig,
  FehlenderArtikel,
  StammdatenAbgleich,
  FreigabeAntwort,
  Frist,
  ImportLauf,
  ImportLaufKurz,
  Importstatus,
  JournalEintrag,
  Inventur,
  KladdePlatz,
  Kopplung,
  InventurBlatt,
  Jahreszeile,
  Kladde,
  Lieferant,
  LieferantEingabe,
  MailAbsender,
  MailEingang,
  PickupJob,
  PickupStatus,
  PlanIst,
  Planmenge,
  PostfachStatus,
  Quittung,
  QuittungEingabe,
  PlanmengenExcelAntwort,
  Produktionsplan,
  ProduktionsplanEingabe,
  Regel,
  RegelRueckbau,
  RegelVorschlag,
  RegelVorschlagAntwort,
  ReportStand,
  Rechenbasis,
  RechenbasisEinstellung,
  ReichweiteAnfrage,
  ReichweiteErgebnis,
  Rueckfrage,
  RueckfrageWirkung,
  Sperrung,
  SperrungEingabe,
  Staffel,
  Takt,
  TaktEinstellung,
  Uebersicht,
  Unterhaltung,
  UnterhaltungVoll,
  Vermerk,
  Vermerkart,
  Vorgabe,
  VorgabeEingabe,
  Waechter,
  ZaehlzettelAntwort,
  Zweitblick,
} from './typen'

const BASIS = (import.meta.env.VITE_API_BASIS ?? '').replace(/\/$/, '')

export class ApiFehler extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

// Wie viele Anfragen gerade laufen - der Ladebalken im Kopf hört darauf.
let laufende = 0
const ladeHoerer = new Set<(anzahl: number) => void>()
function ladung(schritt: number) {
  laufende += schritt
  for (const h of ladeHoerer) h(laufende)
}
/** Auf laufende Anfragen hören; gibt die Abmeldung zurück. */
export function beiLadung(hoerer: (anzahl: number) => void): () => void {
  ladeHoerer.add(hoerer)
  hoerer(laufende)
  return () => {
    ladeHoerer.delete(hoerer)
  }
}

/** Eine Anfrage mit Ladezähler. Exportiert, damit api_notierungen.ts dieselbe Fehlerbehandlung nutzt. */
export async function hole<T>(pfad: string, optionen?: RequestInit): Promise<T> {
  ladung(1)
  try {
    return await holeOhneZaehler<T>(pfad, optionen)
  } finally {
    ladung(-1)
  }
}

// Läuft eine Sitzung ab, während jemand arbeitet, kommt auf jede Anfrage ein 401. Die Oberfläche
// soll dann den Anmeldeschirm zeigen und nicht zehn rote Meldungen (20.09.2026).
const abmeldeHoerer = new Set<() => void>()
/** Auf „die Sitzung ist zu Ende" hören; gibt die Abmeldung zurück. */
export function beiSitzungsende(hoerer: () => void): () => void {
  abmeldeHoerer.add(hoerer)
  return () => {
    abmeldeHoerer.delete(hoerer)
  }
}

// Fehlt dem Benutzer das Recht auf einen Bereich, antwortet das Backend mit 403 „Kein Zugang zum Bereich …".
// Das ist keine rote Meldung auf einer Seite, sondern eine eigene Seite mit dem Weg zur Bereichsauswahl
// (06.10.2026). Ein 403 aus anderem Grund (Rolle) bleibt eine Meldung.
const zugangHoerer = new Set<(text: string) => void>()
/** Auf „kein Zugang zu diesem Bereich" hören; gibt die Abmeldung zurück. */
export function beiKeinZugang(hoerer: (text: string) => void): () => void {
  zugangHoerer.add(hoerer)
  return () => {
    zugangHoerer.delete(hoerer)
  }
}

/** Ist der Fehler die Bereichssperre des Backends? */
export function istKeinZugang(f: unknown): boolean {
  return f instanceof ApiFehler && f.status === 403 && /kein zugang zum bereich/i.test(f.message)
}

/** Status prüfen, Hörer benachrichtigen, Fehler werfen - für jede Antwort gleich. */
async function pruefen(pfad: string, antwort: Response): Promise<void> {
  if (antwort.status === 401 && !pfad.startsWith('/api/anmeldung')) {
    for (const h of abmeldeHoerer) h()
  }
  if (antwort.ok) return
  const fehler = await fehlerAus(antwort)
  if (istKeinZugang(fehler)) for (const h of zugangHoerer) h(fehler.message)
  throw fehler
}

async function holeOhneZaehler<T>(pfad: string, optionen?: RequestInit): Promise<T> {
  const antwort = await fetch(`${BASIS}${pfad}`, {
    headers: { 'Content-Type': 'application/json' },
    ...optionen,
  })
  await pruefen(pfad, antwort)
  // 204 (DELETE) hat keinen Körper.
  if (antwort.status === 204) return undefined as T
  return (await antwort.json()) as T
}

/** Der Statuscode als deutscher Satz - kein nacktes „Not Found" in einer Meldung (Befund 18.09.2026). */
const STATUSSATZ: Record<number, string> = {
  400: 'Die Anfrage war fehlerhaft.',
  401: 'Dafür fehlt die Anmeldung.',
  403: 'Das ist nicht erlaubt.',
  404: 'Das Backend kennt diese Adresse nicht.',
  409: 'Der Stand hat sich inzwischen geändert.',
  422: 'Die Eingabe passt nicht zu dem, was das Backend erwartet.',
  500: 'Im Backend ist ein Fehler aufgetreten.',
  502: 'Das Backend antwortet nicht.',
  503: 'Das Backend antwortet gerade nicht.',
  504: 'Das Backend hat zu lange gebraucht.',
}

/** Der Fehler einer gescheiterten Antwort. FastAPI legt den Grund in "detail" - den will der Nutzer sehen, nicht "500". */
async function fehlerAus(antwort: Response): Promise<ApiFehler> {
  let text = `${STATUSSATZ[antwort.status] ?? 'Die Anfrage ist fehlgeschlagen.'} (${antwort.status} ${antwort.statusText})`
  try {
    const koerper = await antwort.json()
    const detail: unknown = koerper?.detail
    if (Array.isArray(detail)) {
      // Pydantic-Validierung: eine Liste aus {loc, msg} - als Saetze, nicht als [object Object].
      text = detail
        .map((e: { loc?: unknown[]; msg?: string }) =>
          typeof e === 'string' ? e : `${(e.loc ?? []).slice(1).join('.')}: ${e.msg ?? ''}`.replace(/^: /, ''),
        )
        .join('; ')
    } else if (detail) text = String(detail)
  } catch {
    // Antwort war kein JSON - dann bleibt der Statustext stehen.
  }
  return new ApiFehler(text, antwort.status)
}

/** Ein Formular als multipart schicken. Ohne Content-Type-Kopf, damit der Browser die Grenze selbst setzt. */
export async function schickeFormular<T>(pfad: string, formular: FormData): Promise<T> {
  ladung(1)
  try {
    const antwort = await fetch(`${BASIS}${pfad}`, { method: 'POST', body: formular })
    await pruefen(pfad, antwort)
    return (await antwort.json()) as T
  } finally {
    ladung(-1)
  }
}

/** Eine Datei als multipart hochladen (Feld `datei`). */
function ladeHoch<T>(pfad: string, datei: File): Promise<T> {
  const formular = new FormData()
  formular.append('datei', datei)
  return schickeFormular<T>(pfad, formular)
}

/** Der Text einer gescheiterten Anfrage fuer eine Meldung - egal, ob Error, ApiFehler oder etwas anderes flog. */
export function fehlertext(f: unknown): string {
  if (f instanceof Error) return f.message
  return String(f)
}

/** Was `ohne404` liefert, wenn das Backend den Endpunkt (noch) nicht kennt. */
export const NICHT_VORHANDEN = Symbol('nicht vorhanden')

/**
 * Eine Auswertung holen, die ein älteres Backend noch nicht kennt: ein 404 ist dann kein Fehler,
 * sondern eine Auskunft - die Karte zeigt einen Satz statt einer roten Meldung. Alles andere fliegt
 * weiter und landet als Fehlertext auf der Seite.
 */
export async function ohne404<T>(anfrage: Promise<T>): Promise<T | typeof NICHT_VORHANDEN> {
  try {
    return await anfrage
  } catch (f) {
    if (f instanceof ApiFehler && f.status === 404) return NICHT_VORHANDEN
    throw f
  }
}

/** Pfad mit Suchparametern; leere Werte bleiben weg. */
export function mitSuche(pfad: string, parameter: Record<string, string | number | null | undefined>): string {
  const suche = new URLSearchParams()
  for (const [name, wert] of Object.entries(parameter)) {
    if (wert !== undefined && wert !== null && wert !== '') suche.set(name, String(wert))
  }
  return suche.toString() ? `${pfad}?${suche}` : pfad
}

/** DELETE ohne Antwortkörper - FastAPI liefert 204. */
export function loesche(pfad: string): Promise<void> {
  return hole<void>(pfad, { method: 'DELETE' })
}

export const api = {
  gruppen: () => hole<string[]>('/api/kladde/gruppen'),

  // Postfach / Einstellungen
  postfachStatus: () => hole<PostfachStatus>('/api/postfach/status'),
  postfachAbsender: () => hole<MailAbsender[]>('/api/postfach/absender'),
  postfachAbsenderAnlegen: (adresse: string, name: string | null) =>
    hole<MailAbsender>('/api/postfach/absender', { method: 'POST', body: JSON.stringify({ adresse, name }) }),
  postfachAbsenderLoeschen: (id: number) => loesche(`/api/postfach/absender/${id}`),
  /** Die letzten Mails; mit `bereich` nur die, die in diesen Bereich sortiert wurden. */
  postfachEingaenge: (limit = 50, bereich?: BereichId) =>
    hole<MailEingang[]>(mitSuche('/api/postfach/eingaenge', { limit, bereich })),
  /** Eine Mail von Hand einem Bereich zuordnen - das Backend verarbeitet sie dann dort neu. */
  postfachBereich: (id: number, bereich: BereichId) =>
    hole<MailEingang>(`/api/postfach/eingaenge/${id}/bereich`, { method: 'POST', body: JSON.stringify({ bereich }) }),
  postfachAbholen: () => hole<PostfachStatus>('/api/postfach/abholen', { method: 'POST' }),
  postfachErneut: (id: number) => hole<MailEingang>(`/api/postfach/eingaenge/${id}/verarbeiten`, { method: 'POST' }),

  // SelectLine-Abholdienst: der zweite Weg, auf dem Daten hereinkommen - siehe SelectLineDienst.tsx.
  pickupStatus: () => hole<PickupStatus>('/api/selectline/status'),
  pickupJobs: (limit = 20) => hole<PickupJob[]>(`/api/selectline/jobs?limit=${limit}`),
  createPickupJob: (kind: string, parameters: Record<string, string>) =>
    hole<PickupJob>('/api/selectline/jobs', { method: 'POST', body: JSON.stringify({ kind, parameters }) }),
  retryPickupJob: (id: number) =>
    hole<PickupJob>(`/api/selectline/jobs/${id}/retry`, { method: 'POST' }),
  /** Einen offenen oder laufenden Abruf stoppen; er bleibt mit Vermerk in der Liste (02.11.2026). 409, wenn schon fertig. */
  abbrechenPickupJob: (id: number) =>
    hole<PickupJob>(`/api/selectline/jobs/${id}/abbrechen`, { method: 'POST' }),
  /** Der letzte Abgleich der Bestellungen mit SelectLine - u. a. Abrufe, deren Wareneingang noch fehlt. */
  bestellabgleich: () => hole<Bestellabgleich>('/api/selectline/bestellabgleich'),

  /** Einen fehlgeschlagenen Abruf verwerfen - er verschwindet aus „fehlgeschlagene Abrufe". */
  discardPickupJob: (id: number) => loesche(`/api/selectline/jobs/${id}`),
  setPickupMode: (mode: PickupStatus['mode']) =>
    hole<PickupStatus>('/api/selectline/mode', { method: 'PUT', body: JSON.stringify({ mode }) }),

  /** Ohne `rechenbasis` rechnet das Backend mit der Einstellung; ohne `stichtag`/`horizontTage`/`takt` mit dem
   * Takt für alle (`taktStandard`, Runde 4 02.11.2026). */
  disposition: (gruppe: string, stichtag?: string, horizontTage?: number, rechenbasis?: string, takt?: Takt) =>
    hole<Disposition>(
      mitSuche(`/api/disposition/${encodeURIComponent(gruppe)}`, {
        stichtag,
        horizont_tage: horizontTage,
        rechenbasis,
        takt,
      }),
    ),
  /** Womit Disposition, Heute, Fällig-Liste und Assistent rechnen, wenn niemand eine Rechenbasis nennt. */
  rechenbasisStandard: () => hole<RechenbasisEinstellung>('/api/disposition/rechenbasis'),
  rechenbasisStandardSetzen: (rechenbasis: Rechenbasis) =>
    hole<RechenbasisEinstellung>('/api/disposition/rechenbasis', { method: 'PUT', body: JSON.stringify({ rechenbasis }) }),
  /** Nach welchem Takt Blatt, Export, Heute, Fällig-Liste und Assistent Stichtag/Horizont nehmen, wenn niemand einen nennt. */
  taktStandard: () => hole<TaktEinstellung>('/api/disposition/takt'),
  taktStandardSetzen: (takt: Takt) =>
    hole<TaktEinstellung>('/api/disposition/takt', { method: 'PUT', body: JSON.stringify({ takt }) }),
  /** „Was wäre, wenn": Reichweite mit angenommenem Bestand und Zugängen - ändert nichts. */
  reichweiteRechnen: (daten: ReichweiteAnfrage) =>
    hole<ReichweiteErgebnis>('/api/disposition/reichweite', { method: 'POST', body: JSON.stringify(daten) }),

  importeLetzte: () => hole<ReportStand[]>('/api/importe/letzte'),

  /** Forecast des Chefs aus der Datei „4. PLAN - IST" übernehmen (Spalten „FC GF JJJJ"). */
  planmengenExcel: (datei: File, jahr?: number) =>
    ladeHoch<PlanmengenExcelAntwort>(mitSuche('/api/disposition/planmengen/excel', { jahr }), datei),

  planSetzen: (daten: ProduktionsplanEingabe) =>
    hole<Produktionsplan>('/api/disposition/plan', { method: 'POST', body: JSON.stringify(daten) }),

  planLoeschen: (id: number) => loesche(`/api/disposition/plan/${id}`),

  bestellungAnlegen: (daten: BestellungEingabe) =>
    hole<Bestellung>('/api/disposition/bestellungen', { method: 'POST', body: JSON.stringify(daten) }),

  bestellungAendern: (id: number, daten: BestellungEingabe) =>
    hole<Bestellung>(`/api/disposition/bestellungen/${id}`, {
      method: 'PUT',
      body: JSON.stringify(daten),
    }),

  bestellungLoeschen: (id: number) => loesche(`/api/disposition/bestellungen/${id}`),
  /** Die Frage des Bestellabgleichs beantworten, ohne die Bestellung zu ändern - „stimmt so". */
  bestellungGeprueft: (id: number) =>
    hole<Bestellung>(`/api/disposition/bestellungen/${id}/geprueft`, { method: 'POST' }),

  /** Einen Eintrag auf Heute/Fällig-Liste als erledigt bzw. zur Kenntnis genommen markieren (Runde 4, 02.11.2026). */
  erledigen: (schluessel: string, fingerabdruck: string, grund?: string) =>
    hole<Erledigung>('/api/erledigt', { method: 'POST', body: JSON.stringify({ schluessel, fingerabdruck, grund }) }),
  erledigungenAufheben: (id: number) => hole<Erledigung>(`/api/erledigt/${id}`, { method: 'DELETE' }),

  sperrungAnlegen: (daten: SperrungEingabe) =>
    hole<Sperrung>('/api/disposition/sperrungen', { method: 'POST', body: JSON.stringify(daten) }),

  sperrungAufheben: (id: number) => loesche(`/api/disposition/sperrungen/${id}`),

  kladde: (gruppe: string, von?: string, bis?: string) =>
    hole<Kladde>(mitSuche(`/api/kladde/${encodeURIComponent(gruppe)}`, { von, bis })),

  // --- Lieferanten, Fristen, Planmengen, Vermerke, Bestellvorschlag, Export ---------------------

  // --- Anmeldung ---------------------------------------------------------------------------
  /** Anmelden; der Server setzt das Sitzungscookie. */
  anmelden: (name: string, passwort: string) =>
    hole<Benutzer>('/api/anmeldung', { method: 'POST', body: JSON.stringify({ name, passwort }) }),
  abmelden: () => hole<{ abgemeldet: boolean }>('/api/anmeldung/abmelden', { method: 'POST' }),
  /** Wer angemeldet ist - wirft ApiFehler 401, wenn niemand. */
  ich: () => hole<Benutzer>('/api/anmeldung/ich'),
  /** Alle Zugänge - nur Rolle verwaltung. */
  benutzerListe: () => hole<Benutzer[]>('/api/anmeldung/benutzer'),
  /** Welche Bereiche ein Benutzer öffnen darf - nur Rolle verwaltung. */
  benutzerBereiche: (name: string, bereiche: BereichId[]) =>
    hole<Benutzer>(`/api/anmeldung/benutzer/${encodeURIComponent(name)}/bereiche`, {
      method: 'PUT',
      body: JSON.stringify({ bereiche }),
    }),
  passwortAendern: (altes: string, neues: string) =>
    hole<Benutzer>('/api/anmeldung/passwort', {
      method: 'PUT',
      body: JSON.stringify({ altes_passwort: altes, neues_passwort: neues }),
    }),

  lieferanten: () => hole<Lieferant[]>('/api/lieferanten'),

  lieferantSpeichern: (daten: LieferantEingabe) =>
    hole<Lieferant>('/api/lieferanten', { method: 'POST', body: JSON.stringify(daten) }),

  staffelnSetzen: (id: number, staffeln: Staffel[], artikelgruppe?: string) =>
    hole<Lieferant>(mitSuche(`/api/lieferanten/${id}/staffeln`, { artikelgruppe }), {
      method: 'PUT',
      body: JSON.stringify(staffeln),
    }),

  lieferantLoeschen: (id: number, mitArtikeln = false) =>
    loesche(`/api/lieferanten/${id}${mitArtikeln ? '?mit_artikeln=true' : ''}`),
  lieferantArtikel: (id: number, artikelnummern: string[]) =>
    hole<Lieferant>(`/api/lieferanten/${id}/artikel`, { method: 'POST', body: JSON.stringify(artikelnummern) }),

  /** Artikelgruppe anlegen oder ändern (mit `id` auch umbenennen); das ganze Formular, null = geerbt. */
  gruppeSpeichern: (lieferantId: number, daten: ArtikelgruppeEingabe) =>
    hole<Lieferant>(`/api/lieferanten/${lieferantId}/gruppen`, { method: 'POST', body: JSON.stringify(daten) }),
  /** Gruppe löschen: ihre Artikel bleiben beim Lieferanten, ohne Gruppe. */
  gruppeLoeschen: (lieferantId: number, gruppeId: number) =>
    hole<Lieferant>(`/api/lieferanten/${lieferantId}/gruppen/${gruppeId}`, { method: 'DELETE' }),
  /** Die Mitglieder einer Gruppe ersetzen - jeder Artikel muss schon diesem Lieferanten zugeordnet sein. */
  gruppeArtikelSetzen: (lieferantId: number, gruppeId: number, artikelnummern: string[]) =>
    hole<Lieferant>(`/api/lieferanten/${lieferantId}/gruppen/${gruppeId}/artikel`, {
      method: 'PUT',
      body: JSON.stringify(artikelnummern),
    }),

  fristen: (material?: string) => hole<Frist[]>(mitSuche('/api/disposition/fristen', { material })),

  fristAnlegen: (daten: { material: string; verwendbar_bis: string; grund: string; menge_t?: number | null; vorlauf_tage?: number | null; bestaetigt?: boolean }) =>
    hole<Frist>('/api/disposition/fristen', { method: 'POST', body: JSON.stringify(daten) }),
  /** Festhalten, ob eine Frist geklärt ist; `bemerkung` leer = sie bleibt, wie sie ist. */
  fristKlaerung: (id: number, bestaetigt: boolean, bemerkung?: string | null) =>
    hole<Frist>(`/api/disposition/fristen/${id}/klaerung`, {
      method: 'PUT',
      body: JSON.stringify({ bestaetigt, bemerkung: bemerkung ?? null }),
    }),

  fristAufheben: (id: number) => loesche(`/api/disposition/fristen/${id}`),

  planmengen: (fertigartikel?: string, jahr?: number, quelle?: string) =>
    hole<Planmenge[]>(mitSuche('/api/disposition/planmengen', { fertigartikel, jahr, quelle })),

  planIst: (jahr?: number, gruppe?: string) =>
    hole<PlanIst[]>(mitSuche('/api/disposition/plan-ist', { jahr, gruppe })),

  planmengenSetzen: (daten: Planmenge[]) =>
    hole<Planmenge[]>('/api/disposition/planmengen', { method: 'PUT', body: JSON.stringify(daten) }),

  vermerke: (artikel?: string) => hole<Vermerk[]>(mitSuche('/api/vermerke', { artikel })),

  vermerkAnlegen: (daten: { artikel: string | null; art: Vermerkart; text: string; tag?: string | null; bis?: string | null; faellig?: string | null }) =>
    hole<Vermerk>('/api/vermerke', { method: 'POST', body: JSON.stringify(daten) }),
  vermerkErledigt: (id: number, erledigt = true) =>
    hole<Vermerk>(mitSuche(`/api/vermerke/${id}/erledigt`, { erledigt: String(erledigt) }), { method: 'POST' }),

  vermerkLoeschen: (id: number) => loesche(`/api/vermerke/${id}`),

  bestellvorschlaege: (status?: string) => hole<Bestellvorschlag[]>(mitSuche('/api/bestellvorschlaege', { status })),

  bestellvorschlagRechnen: (daten: {
    lieferant: string
    art: 'abruf' | 'nachauflage'
    stichtag?: string
    zielreichweite_tage: number
    gruppe?: string | null
    /** nur die Artikel dieser Artikelgruppe des Lieferanten */
    artikelgruppe?: string | null
  }) => hole<Bestellvorschlag>('/api/bestellvorschlaege', { method: 'POST', body: JSON.stringify(daten) }),

  bestellvorschlagEntscheiden: (id: number, variante: number, bemerkung?: string) =>
    hole<Bestellvorschlag>(`/api/bestellvorschlaege/${id}/entscheiden`, {
      method: 'POST',
      body: JSON.stringify({ variante, bemerkung: bemerkung ?? null }),
    }),

  /** Paletten je Position von Hand setzen (0 = weglassen; nicht genannte bleiben) - rechnet die Variante neu. */
  varianteAufteilen: (id: number, variante: number, paletten: Record<string, number>) =>
    hole<Bestellvorschlag>(`/api/bestellvorschlaege/${id}/varianten/${variante}`, {
      method: 'PUT',
      body: JSON.stringify({ paletten }),
    }),

  bestellvorschlagEmpfehlung: (id: number) =>
    hole<Bestellvorschlag>(`/api/bestellvorschlaege/${id}/empfehlung`, { method: 'POST' }),
  bestellvorschlagVerwerfen: (id: number) =>
    hole<Bestellvorschlag>(`/api/bestellvorschlaege/${id}/verwerfen`, { method: 'POST' }),

  /** Download-Adressen fuer den Excel-Export - der Browser laedt sie als Datei. */
  exportKladde: (gruppe?: string, von?: string, bis?: string) =>
    BASIS + mitSuche('/api/export/kladde.xlsx', { gruppe, von, bis }),
  /** Ohne `rechenbasis`/`stichtag`/`takt` gilt auch im Export die Einstellung bzw. der Takt für alle. */
  exportDisposition: (gruppe?: string, stichtag?: string, rechenbasis?: string, takt?: Takt) =>
    BASIS + mitSuche('/api/export/disposition.xlsx', { gruppe, stichtag, rechenbasis, takt }),
  exportFaellig: (ab?: string, bis?: string) => BASIS + mitSuche('/api/export/faellig.xlsx', { ab, bis }),
  exportBestandsliste: (inventurId: number, gruppe?: string) =>
    BASIS + mitSuche(`/api/export/bestandsliste/${inventurId}.xlsx`, { gruppe }),
  exportAuswertung: (jahr: number, gruppe?: string) =>
    BASIS + mitSuche(`/api/export/inventur-auswertung/${jahr}.xlsx`, { gruppe }),
  exportBestellvorschlag: (id: number) => `${BASIS}/api/export/bestellvorschlag/${id}.xlsx`,
  exportPlanIst: (jahr: number) => `${BASIS}/api/export/plan-ist/${jahr}.xlsx`,

  rueckfragen: (importLaufId?: number, status?: string) =>
    hole<Rueckfrage[]>(mitSuche('/api/rueckfragen', { import_lauf_id: importLaufId, status })),

  rueckfrageBeantworten: (id: number, antwort: string) =>
    hole<Rueckfrage>(`/api/rueckfragen/${id}/antwort`, {
      method: 'POST',
      body: JSON.stringify({ antwort }),
    }),

  rueckfrageAnwenden: (id: number) =>
    hole<RueckfrageWirkung>(`/api/rueckfragen/${id}/anwenden`, { method: 'POST' }),

  rueckfrageVerwerfen: (id: number) =>
    hole<Rueckfrage>(`/api/rueckfragen/${id}/verwerfen`, { method: 'POST' }),

  importe: (status?: Importstatus) => hole<ImportLaufKurz[]>(mitSuche('/api/importe', { status })),

  importLauf: (id: number) => hole<ImportLauf>(`/api/importe/${id}`),
  zweitblick: (id: number) => hole<Zweitblick[]>(`/api/importe/${id}/zweitblick`),
  excelHochladen: (datei: File) => ladeHoch<ExcelImport>('/api/importe/excel', datei),
  /** Warten, bis der Plan eines Laufs gelesen ist - der Server liest im Hintergrund, damit keine lange Verbindung
   *  offen bleibt (Netzwerkwechsel im Browser, Lesezeit des Proxys). Alle drei Sekunden nachsehen, höchstens zehn Minuten. */
  excelWarten: async (id: number): Promise<ExcelImport> => {
    let netzfehler = 0
    for (let versuch = 0; versuch < 200; versuch++) {
      try {
        const lauf = await hole<ExcelImport>(`/api/importe/excel/${id}`)
        if (lauf.fehler) throw new Error(lauf.fehler)
        if (lauf.bereit) return lauf
        netzfehler = 0
      } catch (f) {
        // Ein Netzwerkwechsel (WLAN, Mobilfunk) bricht eine einzelne Abfrage ab - der Lauf läuft weiter.
        // Erst nach fünf Fehlschlägen in Folge oder bei einer Antwort des Servers aufgeben.
        if (f instanceof ApiFehler || ++netzfehler >= 5) throw f
      }
      await new Promise((r) => setTimeout(r, 3000))
    }
    throw new Error('Der Plan wurde nach zehn Minuten nicht fertig gelesen.')
  },
  excelImport: (id: number) => hole<ExcelImport>(`/api/importe/excel/${id}`),
  excelPlanAendern: (id: number, blaetter: { blatt: string; art?: string; gruppe?: string; aktiv?: boolean; kopplungen?: Record<string, string[]> }[]) =>
    hole<ExcelImport>(`/api/importe/excel/${id}`, { method: 'PUT', body: JSON.stringify({ blaetter }) }),
  excelAusfuehren: (id: number) => hole<ExcelImport>(`/api/importe/excel/${id}/ausfuehren`, { method: 'POST' }),

  fehlendeArtikel: (id: number) =>
    hole<FehlenderArtikel[]>(`/api/importe/${id}/fehlende-artikel`),

  /** Artikel, die in SelectLine Bestand haben, aber hier keinen Stammsatz - mit Vorschlag. */
  stammdatenAbgleich: () => hole<StammdatenAbgleich>('/api/selectline/fehlende-artikel'),

  neuPruefen: (id: number) =>
    hole<ImportLauf>(`/api/importe/${id}/neu-pruefen`, { method: 'POST' }),

  freigeben: (id: number, positionenAbwaehlen: number[] = []) =>
    hole<FreigabeAntwort>(`/api/importe/${id}/freigabe`, {
      method: 'POST',
      body: JSON.stringify({ benutzer: 'dashboard', positionen_abwaehlen: positionenAbwaehlen }),
    }),

  /** Eine gebuchte Freigabe zurückdrehen: Bewegungen, Produktion, Plan weg, Bestellungen wieder wie vorher. */
  zuruecknehmen: (id: number) =>
    hole<Ruecknahme>(`/api/importe/${id}/zuruecknehmen`, {
      method: 'POST',
      body: JSON.stringify({ benutzer: 'dashboard', positionen_abwaehlen: [] }),
    }),

  verwerfen: (id: number) =>
    hole<{ status: string }>(`/api/importe/${id}/verwerfen`, {
      method: 'POST',
      body: JSON.stringify({ benutzer: 'dashboard', positionen_abwaehlen: [] }),
    }),

  hochladen: (datei: File) => ladeHoch<ImportLaufKurz>('/api/importe/upload', datei),

  erfassungVorlagen: () => hole<ErfassungVorlage[]>('/api/erfassung/vorlagen'),

  erfassungVorschlag: (vorlage: string, text: string, tag?: string) =>
    hole<ErfassungAntwort>('/api/erfassung/vorschlag', {
      method: 'POST',
      body: JSON.stringify({ vorlage, text, tag: tag ?? null }),
    }),

  artikel: (gruppe?: string) => hole<Artikel[]>(mitSuche('/api/stammdaten/artikel', { gruppe })),

  artikelAnlegen: (daten: ArtikelEingabe) =>
    hole<Artikel>('/api/stammdaten/artikel', { method: 'POST', body: JSON.stringify(daten) }),

  artikelAendern: (artikelnummer: string, daten: ArtikelEingabe) =>
    hole<Artikel>(`/api/stammdaten/artikel/${encodeURIComponent(artikelnummer)}`, {
      method: 'PUT',
      body: JSON.stringify(daten),
    }),

  /** Wo ein Artikel in der Kladde steht - je Blatt Block, Spalte, Position, Nachbarn; leer = in keinem Blatt. */
  kladdePlatz: (artikelnummer: string) =>
    hole<KladdePlatz[]>(`/api/stammdaten/artikel/${encodeURIComponent(artikelnummer)}/kladde-platz`),
  /** Hinter einen anderen Artikel setzen; `gemeinsam` = in dessen Block mit einer Produktionsspalte (zwei Fertigartikel). */
  einordnen: (artikelnummer: string, nachArtikel: string, gemeinsam: boolean) =>
    hole<EinordnenAntwort>(`/api/stammdaten/artikel/${encodeURIComponent(artikelnummer)}/einordnen`, {
      method: 'POST',
      body: JSON.stringify({ nach_artikel: nachArtikel, gemeinsam }),
    }),

  /** Kopplungen Material - Fertigartikel mit Gültigkeit; Filter nach Material oder Fertigartikel. */
  kopplungen: (filter: { material?: string; fertigartikel?: string } = {}) =>
    hole<Kopplung[]>(mitSuche('/api/stammdaten/kopplung', filter)),
  /** Anlegen - oder bei einem schon gekoppelten Paar die Gültigkeit setzen. null = offen. */
  kopplungSetzen: (daten: { material: string; fertigartikel: string; gueltig_ab: string | null; gueltig_bis: string | null }) =>
    hole<Kopplung>('/api/stammdaten/kopplung', { method: 'POST', body: JSON.stringify(daten) }),
  kopplungAendern: (id: number, daten: { gueltig_ab: string | null; gueltig_bis: string | null }) =>
    hole<Kopplung>(`/api/stammdaten/kopplung/${id}`, { method: 'PUT', body: JSON.stringify(daten) }),
  kopplungLoeschen: (id: number) => loesche(`/api/stammdaten/kopplung/${id}`),

  artikelSammelAnlegen: (artikel: ArtikelEingabe[]) =>
    hole<ArtikelSammelAntwort>('/api/stammdaten/artikel/mehrere', {
      method: 'POST',
      body: JSON.stringify({ artikel }),
    }),

  inventuren: () => hole<Inventur[]>('/api/inventur'),

  inventurAnlegen: (stichtag: string, beleg?: string | null) =>
    hole<Inventur>('/api/inventur', { method: 'POST', body: JSON.stringify({ stichtag, beleg: beleg ?? null }) }),

  inventur: (id: number, gruppe?: string) => hole<InventurBlatt>(mitSuche(`/api/inventur/${id}`, { gruppe })),

  zaehlungSpeichern: (
    id: number,
    artikelnummer: string,
    positionen: { bezeichnung: string; anzahl: number | null }[],
    vermerk: string | null,
  ) =>
    hole<unknown>(`/api/inventur/${id}/zaehlung/${encodeURIComponent(artikelnummer)}`, {
      method: 'PUT',
      body: JSON.stringify({ positionen, vermerk }),
    }),

  zaehlzettel: (id: number, gruppe: string | null, text: string) =>
    hole<ZaehlzettelAntwort>(`/api/inventur/${id}/zaehlzettel`, {
      method: 'POST',
      body: JSON.stringify({ gruppe, text }),
    }),

  sollAktualisieren: (id: number) =>
    hole<Inventur>(`/api/inventur/${id}/soll-aktualisieren`, { method: 'POST' }),

  inventurAbschliessen: (id: number) => hole<Inventur>(`/api/inventur/${id}/abschliessen`, { method: 'POST' }),

  jahresuebersicht: (jahr: number, gruppe?: string) => hole<Jahreszeile[]>(mitSuche(`/api/inventur/jahr/${jahr}`, { gruppe })),

  stichtagVorschlag: () => hole<{ stichtag: string }>('/api/inventur/stichtag/vorschlag'),

  regeln: () => hole<Regel[]>('/api/regeln'),

  regelVorschlag: (text: string) =>
    hole<RegelVorschlagAntwort>('/api/regeln/vorschlag', {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),

  regelSpeichern: (vorschlag: RegelVorschlag, freitext: string) =>
    hole<Regel>('/api/regeln', {
      method: 'POST',
      body: JSON.stringify({
        art: vorschlag.art,
        titel: vorschlag.titel,
        parameter: vorschlag.parameter,
        freitext,
        begruendung: vorschlag.begruendung,
      }),
    }),

  /** Abschalten baut die Wirkung zurück; 409, wenn eine Stelle seitdem anders geändert wurde - `erzwingen` lässt sie stehen. */
  regelAktiv: (id: number, aktiv: boolean, erzwingen = false) =>
    hole<Regel>(`/api/regeln/${id}/aktiv?aktiv=${aktiv}&erzwingen=${erzwingen}`, { method: 'POST' }),

  /** Löschen stellt den Stand vor der Regel wieder her und sagt, was zurückgesetzt wurde. */
  regelLoeschen: (id: number, erzwingen = false) =>
    hole<RegelRueckbau>(`/api/regeln/${id}?erzwingen=${erzwingen}`, { method: 'DELETE' }),

  uebersichtOffen: (stichtag?: string) => hole<Uebersicht>(mitSuche('/api/uebersicht/offen', { stichtag })),
  beobachtungen: (stichtag?: string, gruppe?: string) =>
    hole<Beobachtung>(mitSuche('/api/disposition/beobachtungen', { stichtag, gruppe })),
  beobachtungenNeu: (stichtag?: string, gruppe?: string) =>
    hole<Beobachtung>(mitSuche('/api/disposition/beobachtungen/neu', { stichtag, gruppe }), { method: 'POST' }),
  beobachtungRueckmeldung: (id: number, nr: number, stimmt: boolean, grund?: string) =>
    hole<Beobachtung>(`/api/disposition/beobachtungen/${id}/rueckmeldung`, {
      method: 'POST',
      body: JSON.stringify({ nr, stimmt, grund: grund ?? null }),
    }),
  faellig: (ab?: string, bis?: string) => hole<Faellig>(mitSuche('/api/uebersicht/faellig', { ab, bis })),

  /** Die Gespräche eines Bereichs - jeder Bereich hat seinen eigenen Assistenten. */
  unterhaltungen: (bereich: BereichId = 'verpackung') =>
    hole<Unterhaltung[]>(mitSuche('/api/assistent/unterhaltungen', { bereich })),

  unterhaltungAnlegen: (bereich: BereichId = 'verpackung') =>
    hole<Unterhaltung>('/api/assistent/unterhaltungen', { method: 'POST', body: JSON.stringify({ bereich }) }),

  unterhaltung: (id: number) => hole<UnterhaltungVoll>(`/api/assistent/unterhaltungen/${id}`),

  unterhaltungLoeschen: (id: number) => loesche(`/api/assistent/unterhaltungen/${id}`),

  // --- Entscheidungen, Datenprüfung, Journal (18.09.2026) ---
  entscheidungen: () => hole<Entscheidung[]>('/api/entscheidungen'),
  entscheidungLoeschen: (id: number) => loesche(`/api/entscheidungen/${id}`),
  datenpruefung: () => hole<Datenpruefung>('/api/datenpruefung'),
  /** Einen Befund (oder einzelne Artikel daraus) als erledigt vermerken - mit Grund, auf Wunsch mit Wiedervorlage. */
  quittieren: (daten: QuittungEingabe) =>
    hole<Quittung[]>('/api/datenpruefung/quittungen', { method: 'POST', body: JSON.stringify(daten) }),
  /** „Wieder öffnen": die Quittung zurücknehmen, der Befund zählt wieder. */
  quittungAufheben: (id: number) => loesche(`/api/datenpruefung/quittungen/${id}`),

  // --- Was der Assistent sich merkt (02.11.2026) ---
  /** Die Vorgaben eines Bereichs; `alle` auch die ausgeschalteten - die Einstellungen zeigen beide. */
  vorgaben: (bereich: BereichId = 'verpackung', alle = true) =>
    hole<Vorgabe[]>(mitSuche('/api/vorgaben', { bereich, alle: alle ? 'true' : undefined })),
  vorgabeAnlegen: (daten: VorgabeEingabe) => hole<Vorgabe>('/api/vorgaben', { method: 'POST', body: JSON.stringify(daten) }),
  /** Teiländerung: nur die mitgegebenen Felder. */
  vorgabeAendern: (id: number, daten: Partial<Pick<Vorgabe, 'text' | 'geltung' | 'bezug' | 'aktiv'>>) =>
    hole<Vorgabe>(`/api/vorgaben/${id}`, { method: 'PUT', body: JSON.stringify(daten) }),
  vorgabeLoeschen: (id: number) => loesche(`/api/vorgaben/${id}`),
  waechter: () => hole<Waechter[]>('/api/waechter'),
  waechterGesehen: (id: number) => hole<Waechter>(`/api/waechter/${id}/gesehen`, { method: 'POST' }),
  waechterLoeschen: (id: number) => loesche(`/api/waechter/${id}`),
  anliegen: () => hole<Anliegen[]>('/api/anliegen'),
  anliegenAendern: (id: number, daten: { status?: AnliegenStatus; antwort?: string | null }) =>
    hole<Anliegen>(`/api/anliegen/${id}`, { method: 'PUT', body: JSON.stringify(daten) }),
  journal: (limit = 50) => hole<JournalEintrag[]>(mitSuche('/api/journal', { limit })),
  /** 200 mit dem Eintrag; 409 mit `detail`, wenn der Stand inzwischen ein anderer ist - der Text kommt als ApiFehler. */
  journalRueckgaengig: (id: number) => hole<JournalEintrag>(`/api/journal/${id}/rueckgaengig`, { method: 'POST' }),
}

/**
 * Einen Strom des Assistenten lesen (Server-Sent Events über POST): je Ereignis ein Aufruf.
 * Die Antwort kommt Stück für Stück, damit Text und Aktionen erscheinen, während sie entstehen.
 */
export async function strom(pfad: string, koerper: unknown, beiEreignis: (e: ChatEreignis) => void): Promise<void> {
  const antwort = await fetch(`${BASIS}${pfad}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: koerper === undefined ? undefined : JSON.stringify(koerper),
  })
  await stromLesen(pfad, antwort, beiEreignis)
}

/**
 * Sich an einen Lauf hängen, der auf dem Server schon arbeitet (02.11.2026): dieselben Ereignisse wie
 * die POST-Ströme, ab jetzt. Läuft keiner, kommt sofort `fertig`. Der Server rechnet weiter, wenn der
 * Browser die Verbindung verliert - ohne das hieß es „abgebrochen", und Frau Barth fragte am nächsten
 * Morgen: „Ist das jetzt schon erledigt?"
 */
export async function stromAnhaengen(unterhaltungId: number, beiEreignis: (e: ChatEreignis) => void, signal?: AbortSignal): Promise<void> {
  const pfad = `/api/assistent/unterhaltungen/${unterhaltungId}/strom`
  const antwort = await fetch(`${BASIS}${pfad}`, { headers: { Accept: 'text/event-stream' }, signal })
  await stromLesen(pfad, antwort, beiEreignis)
}

async function stromLesen(pfad: string, antwort: Response, beiEreignis: (e: ChatEreignis) => void): Promise<void> {
  await pruefen(pfad, antwort)
  if (!antwort.body) throw new ApiFehler('Der Assistent hat keine Antwort gesendet.', antwort.status)
  const leser = antwort.body.getReader()
  const dekoder = new TextDecoder()
  let rest = ''
  for (;;) {
    const { done, value } = await leser.read()
    if (done) break
    rest += dekoder.decode(value, { stream: true })
    const teile = rest.split('\n\n')
    rest = teile.pop() ?? ''
    for (const teil of teile) {
      const zeile = teil.split('\n').find((z) => z.startsWith('data: '))
      if (zeile) beiEreignis(JSON.parse(zeile.slice(6)) as ChatEreignis)
    }
  }
}
